/**
 * Filter Utilities
 * Generic utilities for filtering and matching operations
 * Used by: financial-metrics, detailed_view dashboards
 */

// ============ TYPES ============

export type MatchMode = 'exact' | 'contains' | 'startsWith' | 'endsWith';

export interface FilterRule {
  attributeName: string;
  operator: '=' | '!=' | 'IN' | 'NOT IN' | '>' | '<' | '>=' | '<=';
  attributeValues: any[];
  logicalOperator: 'AND' | 'OR' | '';
}

export interface FilterPayload {
  filter: {
    where: FilterRule[];
    join: Record<string, any>;
    orderBy: Record<string, any>;
  };
}

export interface CrossFilterConfig {
  widgetId: string;
  filterListner?: string | string[];
  crossFilterColumn?: string;
  crossFilterColumns?: string[];
  categoryMatchMode?: MatchMode;
}

export interface LegendSyncData {
  legendSync: boolean;
  selectedIndices: number[];
  selectedLegends: string[];
  selectedColors: string[];
  seriesNames: string[];
  sourceWidgetId: string;
  filterListener: string;
  colorSyncEnabled?: boolean;
  isCategoryFilter?: boolean;
  legendKey?: string;
}

// ============ CATEGORY MATCHING UTILITIES ============

/**
 * Match value against search value using configurable mode
 * Supports: 'exact', 'contains', 'startsWith', 'endsWith'
 */
export function matchValue(
  value: string,
  searchValue: string,
  mode: MatchMode = 'contains'
): boolean {
  const valUpper = String(value ?? '').trim().toUpperCase();
  const searchUpper = String(searchValue ?? '').trim().toUpperCase();

  switch (mode) {
    case 'exact':
      return valUpper === searchUpper;
    case 'startsWith':
      return valUpper.startsWith(searchUpper) || searchUpper.startsWith(valUpper);
    case 'endsWith':
      return valUpper.endsWith(searchUpper) || searchUpper.endsWith(valUpper);
    case 'contains':
    default:
      // Bidirectional: "Q4 2024" matches "2024", and "2024" matches "Q4 2024"
      return valUpper === searchUpper || valUpper.includes(searchUpper) || searchUpper.includes(valUpper);
  }
}

/**
 * Find matching categories from a list
 */
export function findMatchingCategories(
  categories: string[],
  searchValues: string[],
  mode: MatchMode = 'contains'
): string[] {
  return categories.filter((cat) =>
    searchValues.some((searchVal) => matchValue(cat, searchVal, mode))
  );
}

/**
 * Find matching indices from a list
 */
export function findMatchingIndices(
  items: string[],
  searchValues: string[],
  mode: MatchMode = 'contains'
): number[] {
  const matchingIndices: number[] = [];

  items.forEach((item, index) => {
    const isMatch = searchValues.some((searchVal) => matchValue(item, searchVal, mode));
    if (isMatch) {
      matchingIndices.push(index);
    }
  });

  return matchingIndices;
}

// ============ SERIES MATCHING UTILITIES ============

/**
 * Find series indices by names (case-insensitive, partial matching)
 */
export function findIndicesByNames(
  seriesNames: string[],
  targetNames: string[]
): number[] {
  const normalizedTargetNames = targetNames.map((n) => n?.toUpperCase().trim());
  const matchingIndices: number[] = [];

  seriesNames.forEach((seriesName, index) => {
    const normalizedSeriesName = seriesName?.toUpperCase().trim();

    const isMatch = normalizedTargetNames.some((targetName) => {
      // Exact match
      if (normalizedSeriesName === targetName) return true;
      // Partial match (either contains the other)
      if (normalizedSeriesName.includes(targetName) || targetName.includes(normalizedSeriesName)) {
        return true;
      }
      return false;
    });

    if (isMatch) {
      matchingIndices.push(index);
    }
  });

  return matchingIndices;
}

// ============ FILTER PAYLOAD UTILITIES ============

/**
 * Build filter payload for API requests
 */
export function buildFilterPayload(
  filters: Record<string, any>,
  staticFilters: FilterRule[] = []
): FilterPayload {
  const dynamicFilters = Object.entries(filters)
    .map(([key, value]) => {
      const rawValues = Array.isArray(value) ? value.flat(Infinity) : [value];
      const updatedValues = rawValues.filter(
        (v) =>
          v !== null &&
          v !== undefined &&
          v !== '' &&
          !(Array.isArray(v) && v.length === 0)
      );

      if (updatedValues.length === 0) return null;

      return {
        attributeName: key,
        operator: updatedValues.length > 1 ? 'IN' : '=',
        attributeValues: updatedValues,
        logicalOperator: 'AND',
      } as FilterRule;
    })
    .filter((filter): filter is FilterRule => filter !== null);

  // Adjust logical operators
  if (dynamicFilters.length > 0) {
    dynamicFilters[dynamicFilters.length - 1].logicalOperator = '';
    if (staticFilters.length > 0) {
      staticFilters[staticFilters.length - 1].logicalOperator = 'AND';
    }
  } else {
    if (staticFilters.length > 0) {
      staticFilters[staticFilters.length - 1].logicalOperator = '';
    }
  }

  return {
    filter: {
      where: [...staticFilters, ...dynamicFilters],
      join: {},
      orderBy: {},
    },
  };
}

/**
 * Build query params from filters
 */
export function buildQueryParams(filters: Record<string, any>): Record<string, string> {
  const params: Record<string, string> = {};

  for (const key in filters) {
    const value = filters[key];

    if (key === 'slider_range' && Array.isArray(value) && value.length === 2) {
      params['startRange'] = String(parseInt(value[0], 10));
      params['endRange'] = String(parseInt(value[1], 10));
    } else if (key === 'button') {
      params[value[0]] = 'true';
    } else {
      params[key] = Array.isArray(value) ? value.join(',') : String(value);
    }
  }

  return params;
}

/**
 * Normalize filter values (flatten arrays, remove nulls)
 */
export function normalizeFilterValues(values: any): any[] {
  if (!values) return [];
  const rawValues = Array.isArray(values) ? values.flat(Infinity) : [values];
  return rawValues.filter(
    (v) =>
      v !== null &&
      v !== undefined &&
      v !== '' &&
      !(Array.isArray(v) && v.length === 0)
  );
}

// ============ CROSS-FILTER UTILITIES ============

/**
 * Check if a widget should reload based on filter update
 */
export function shouldWidgetReload(
  filterData: any,
  widgetId: string
): boolean {
  if (!filterData?.filterListener) return false;

  const listeners = filterData.filterListener.split(',').map((l: string) => l.trim());
  return listeners.includes(widgetId);
}

/**
 * Get filter listener as array
 */
export function getFilterListenerArray(config: CrossFilterConfig): string[] {
  const listenerConfig = config.filterListner;
  if (!listenerConfig) return [];
  return Array.isArray(listenerConfig) ? listenerConfig : [listenerConfig];
}

/**
 * Build legend sync filter data
 */
export function buildLegendSyncData(
  selectedIndices: number[],
  seriesNames: string[],
  sourceWidgetId: string,
  filterListener: string | string[],
  options: {
    colorSyncEnabled?: boolean;
    selectedColors?: string[];
    isCategoryFilter?: boolean;
    legendKey?: string;
  } = {}
): LegendSyncData {
  const listener = Array.isArray(filterListener) ? filterListener.join(',') : filterListener;
  const selectedLegends = selectedIndices.map((i) => seriesNames[i]).filter(Boolean);

  return {
    legendSync: true,
    selectedIndices,
    selectedLegends,
    selectedColors: options.selectedColors || [],
    seriesNames,
    sourceWidgetId,
    filterListener: listener,
    colorSyncEnabled: options.colorSyncEnabled,
    isCategoryFilter: options.isCategoryFilter,
    legendKey: options.legendKey,
  };
}

/**
 * Build category filter data (for x-axis label clicks)
 */
export function buildCategoryFilterData(
  selectedCategories: string[],
  sourceWidgetId: string,
  filterListener: string | string[],
  legendKey?: string,
): LegendSyncData {
  const listener = Array.isArray(filterListener) ? filterListener.join(',') : filterListener;

  return {
    legendSync: true,
    selectedLegends: selectedCategories,
    selectedIndices: [],
    selectedColors: [],
    seriesNames: [],
    sourceWidgetId,
    filterListener: listener,
    isCategoryFilter: true,
    legendKey,
  };
}

// ============ FILTER STATE UTILITIES ============

/**
 * Merge filter states
 */
export function mergeFilters(
  existingFilters: Record<string, any>,
  newFilters: Record<string, any>
): Record<string, any> {
  return { ...existingFilters, ...newFilters };
}

/**
 * Check if filters are empty
 */
export function isFilterEmpty(filters: Record<string, any>): boolean {
  return Object.keys(filters).length === 0 ||
    Object.values(filters).every((v) => !v || (Array.isArray(v) && v.length === 0));
}

/**
 * Get selected filter keys
 */
export function getSelectedFilterKeys(filters: Record<string, any>): string[] {
  return Object.entries(filters)
    .filter(([_, value]) => value && (!Array.isArray(value) || value.length > 0))
    .map(([key]) => key);
}

/**
 * Remove empty filters
 */
export function removeEmptyFilters(filters: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value && (!Array.isArray(value) || value.length > 0)) {
      result[key] = value;
    }
  });

  return result;
}

// ============ TABLE CROSS-FILTER UTILITIES ============

/**
 * Filter table rows by cross-filter values
 */
export function filterRowsByCrossFilter(
  rows: Record<string, any>[],
  filterColumn: string,
  filterValues: string[],
  mode: MatchMode = 'contains'
): Record<string, any>[] {
  if (!filterValues || filterValues.length === 0) return rows;

  return rows.filter((row) => {
    const cellValue = String(row[filterColumn] || '');
    return filterValues.some((filterVal) => matchValue(cellValue, filterVal, mode));
  });
}

/**
 * Get unique values from a column
 */
export function getUniqueColumnValues(
  rows: Record<string, any>[],
  columnKey: string
): string[] {
  const values = rows.map((row) => row[columnKey]).filter(Boolean);
  return Array.from(new Set(values.map(String)));
}